import { DrawingGlobals, DrawingModes, VARS } from "../Global/variables";
import { performGlobalUndo, performGlobalRedo } from "../Global/UndoController";
import { deselectPanels } from "../Panels/PanelFunctions";
import { clearRulerSelection, getSelectedRuler, requestSelectedRulerDelete } from "../Ruler/RulerStore";

// ✅ Tree copy/paste + ghost preview
import {
  copySelectedTree,
  pasteCopiedTree,
  stopTreePastePreview,
  isTreePastePreviewActive,
} from "../Trees/TreeCopyPaste";

// KeyEventListener handles keyboard shortcuts and events during drawing operations
class KeyEventListener {
  constructor(
    completeShape,
    currentDrawingMode,
    undo,
    redo,
    setDeleteConfirmationVisible,
    selectedIndices,
    cancelDrawing,
    removeRuler,
    cancelSegmentDrawing,
    updateDrawingMode,
    setDeleteObstacle,
    setDeleteSegment,
    setDeletePanels,
    setSelectedIndices,
    setShowPanelSettings
  ) {
    // Assign passed-in callbacks and states
    this.completeShape = completeShape;
    this.currentDrawingMode = currentDrawingMode;
    this.undo = undo;
    this.redo = redo;
    this.deleteConfirmation = setDeleteConfirmationVisible;
    this.selectedindices = selectedIndices;
    this.cancelDrawing = cancelDrawing;
    this.removeRuler = removeRuler;
    this.cancelSegmentDrawing = cancelSegmentDrawing;
    this.updateDrawingMode = updateDrawingMode;
    this.setDeleteObstacle = setDeleteObstacle;
    this.setDeleteSegment = setDeleteSegment;
    this.setDeletePanels = setDeletePanels;
    this.setSelectedIndices = setSelectedIndices;
    this.setShowPanelSettings = setShowPanelSettings;

    this.removeEventListener();

    this.keyDownHandler = this.keyDownHandler.bind(this);
    this.keyUpHandler = this.keyUpHandler.bind(this);

    window.addEventListener("keydown", this.keyDownHandler);
    window.addEventListener("keyup", this.keyUpHandler);
  }

  keyDownHandler(event) {
    // ✅ Don't steal shortcuts while typing
    const t = event.target;
    if (
      t &&
      (t.tagName === "INPUT" ||
        t.tagName === "TEXTAREA" ||
        t.isContentEditable) &&
      event.key !== "Escape"
    ) {
      return;
    }

    // ✅ Tree copy/paste (works in NONE mode too)
    // Ctrl/Cmd + C: copy selected tree + start ghost preview
    if ((event.ctrlKey || event.metaKey) && (event.key === "c" || event.key === "C")) {
      if (VARS?.selectedTree) {
        event.preventDefault();

        copySelectedTree(VARS.selectedTree); // ✅ this starts the ghost preview internally

        // Last-copy-wins semantics like your other systems (optional but consistent)
        VARS.copiedSegment = null;
        VARS.copiedObstacle = null;

        return;
      }
    }

    // Ctrl/Cmd + V: paste tree at ghost position (or current mouse)
    if ((event.ctrlKey || event.metaKey) && (event.key === "v" || event.key === "V")) {
      if (VARS?.copiedTree) {
        event.preventDefault();
        pasteCopiedTree();
        return;
      }
    }

    switch (event.key) {
      case "Enter":
        if (
          this.currentDrawingMode === DrawingModes.SEGMENT &&
          DrawingGlobals.intersectionPoints.length >= 3
        ) {
          this.completeShape();
        }
        break;

      case "Escape":
        // ✅ First priority: cancel tree ghost preview if active
        if (isTreePastePreviewActive()) {
          stopTreePastePreview();
          return;
        }

        // Deselecting a ruler takes priority over the drawing-mode fallbacks.
        if (getSelectedRuler()) {
          clearRulerSelection();
          return;
        }

        // Existing ESC behavior
        if (
          this.currentDrawingMode === DrawingModes.SEGMENT &&
          DrawingGlobals.intersectionPoints.length > 0
        ) {
          this.undo();
        } else if (
          this.currentDrawingMode === DrawingModes.SEGMENT &&
          DrawingGlobals.intersectionPoints.length === 0
        ) {
          this.cancelSegmentDrawing();
        } else if (this.currentDrawingMode === DrawingModes.OBSTACLE) {
          this.cancelDrawing();
        } else if (this.currentDrawingMode === DrawingModes.RULER) {
          this.removeRuler();
        } else if (this.currentDrawingMode === DrawingModes.TRIM) {
          this.updateDrawingMode(DrawingModes.NONE);
        } else if (this.currentDrawingMode === DrawingModes.PANEL) {
          this.updateDrawingMode(DrawingModes.NONE);
        } else if (
          Array.isArray(this.selectedindices?.panelIndex) &&
          this.selectedindices.panelIndex.length > 0
        ) {
          deselectPanels(
            this.selectedindices.panelIndex,
            this.setSelectedIndices,
            this.selectedindices
          );
          if (typeof this.setShowPanelSettings === "function") {
            this.setShowPanelSettings(false);
          }
        }
        break;

      case "z":
      case "Z":
        if (event.ctrlKey || event.metaKey) {
          event.preventDefault();

          if (
            this.currentDrawingMode === DrawingModes.SEGMENT &&
            DrawingGlobals.intersectionPoints.length > 0
          ) {
            this.undo();
          } else {
            performGlobalUndo();
          }
        }
        break;

      case "y":
      case "Y":
        if (event.ctrlKey || event.metaKey) {
          event.preventDefault();

          if (
            this.currentDrawingMode === DrawingModes.SEGMENT &&
            DrawingGlobals.intersectionPoints.length > 0
          ) {
            this.redo();
          } else {
            performGlobalRedo();
          }
        }
        break;

      case "Delete":
      case "Backspace":
        // A selected ruler raises its own confirmation (the modal copy differs
        // from the segment/obstacle/panel one).
        if (getSelectedRuler()) {
          event.preventDefault();
          requestSelectedRulerDelete();
          break;
        }

        if (VARS?.selectedTree) {
          event.preventDefault();
          window.dispatchEvent(
            new CustomEvent("TREE_DELETE_REQUESTED", {
              detail: { tree: VARS.selectedTree },
            })
          );
          break;
        }
       
        if (VARS?.selectedSphere && window.isTreeUiActive === true) {
          event.preventDefault();
          window.removeSphereAndControls?.(VARS.selectedSphere, { recordUndo: true });
          break;
        }
        
        if (this.selectedindices?.obstacleIndex != null) {
          event.preventDefault();
          this.setDeleteObstacle(this.selectedindices.obstacleIndex);
          this.deleteConfirmation(true);
        } else if (
          Array.isArray(this.selectedindices?.panelIndex) &&
          this.selectedindices.panelIndex.length > 0
        ) {
          event.preventDefault();
          this.setDeletePanels(this.selectedindices.panelIndex);
          this.deleteConfirmation(true);
        } else if (this.selectedindices?.segmentIndex != null) {
          event.preventDefault();
          this.setDeleteSegment(this.selectedindices.segmentIndex);
          this.deleteConfirmation(true);
        }
        break;

      case "Control":
        // no-op
        break;

      default:
        break;
    }
  }

  removeEventListener() {
    window.removeEventListener("keydown", this.keyDownHandler);
    window.removeEventListener("keyup", this.keyUpHandler);
  }

  keyUpHandler(event) {}
}

export default KeyEventListener;
